import React, { useState, useEffect } from 'react';
import { amplifyApi } from '../../services/amplifyApi';
import { useBrand } from '../../context/BrandContext';
import {
  Loader2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Calculator,
  Layers,
  Tag,
  DollarSign,
  Lock,
  Edit2,
  Check,
  X
} from 'lucide-react';

function PricingTab() {
  const brand = useBrand();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [config, setConfig] = useState(null);

  // Check if this brand uses fixed pricing mode (no matrix)
  const isFixedPricingMode = brand.features?.pricing?.mode === 'fixed';

  // Preview calculator state
  const [previewCost, setPreviewCost] = useState('15.00');
  const [previewCategory, setPreviewCategory] = useState('');
  const [previewResult, setPreviewResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  // Global margin editing state
  const [editingGlobalMargin, setEditingGlobalMargin] = useState(false);
  const [editMarginValue, setEditMarginValue] = useState(95);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);

    const response = await amplifyApi.getAdminPricing();
    if (response.success) {
      setConfig(response.configuration || {});
    } else {
      setError(response.error || 'Failed to load pricing configuration');
    }

    setLoading(false);
  };

  const handleSaveRule = async (configType, configId, ruleData) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    const response = await amplifyApi.updatePricingRule(configType, configId, ruleData);
    if (response.success) {
      setSuccess(`Rule "${configType}/${configId}" updated successfully`);
      loadConfig();
    } else {
      setError(response.error || 'Failed to update rule');
    }

    setSaving(false);
  };

  const handlePreview = async () => {
    setCalculating(true);
    setPreviewResult(null);

    const response = await amplifyApi.previewPricing(parseFloat(previewCost), previewCategory);
    if (response.success) {
      setPreviewResult(response);
    } else {
      setPreviewResult({ error: response.error });
    }

    setCalculating(false);
  };

  const handleSaveGlobalMargin = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    const response = await amplifyApi.updatePricingRule('GLOBAL', 'global', {
      defaultMargin: parseFloat(editMarginValue)
    });
    if (response.success) {
      setSuccess('Default margin updated successfully');
      setEditingGlobalMargin(false);
      loadConfig();
    } else {
      setError(response.error || 'Failed to update margin');
    }

    setSaving(false);
  };

  const startEditingGlobalMargin = () => {
    setEditMarginValue(globalConfig?.defaultMargin || 95);
    setEditingGlobalMargin(true);
  };

  // Extract different config types from the configuration object
  const tiers = config?.tiers || [];
  const categories = config?.categories || [];
  const floors = config?.floors || [];
  const licenses = config?.licenses || [];
  const globalConfig = config?.global;

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-600">Loading pricing configuration...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">Pricing Configuration</h2>
        <button
          onClick={loadConfig}
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Fixed Pricing Mode Notice */}
      {isFixedPricingMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Lock className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 mb-1">Fixed Pricing Mode</h3>
              <p className="text-amber-800 text-sm mb-4">
                This brand uses a simple flat margin pricing model. The pricing matrix features
                (tiers, category adjustments, price floors) are disabled for this deployment.
              </p>
              <div className="bg-white/60 rounded-lg p-4 border border-amber-200">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-amber-700">Current Margin Rate</span>
                    <div className="text-2xl font-bold text-amber-900">{globalConfig?.defaultMargin || 95}%</div>
                    <span className="text-xs text-amber-600">Editable in Global Settings below</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-amber-700">Example: $10 cost</span>
                    <div className="text-2xl font-bold text-amber-900">
                      ${((10 * (1 + (globalConfig?.defaultMargin || 95) / 100))).toFixed(2)} price
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-amber-600 mt-3">
                To enable the full pricing matrix, set <code className="bg-amber-100 px-1 rounded">FEATURE_PRICING_MODE=matrix</code> in the Lambda environment variables.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Global Config */}
      {globalConfig && (
        <div className={`border rounded-lg p-4 ${isFixedPricingMode ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200'}`}>
          <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Global Settings
            {isFixedPricingMode && <span className="text-xs text-amber-600 font-normal">(primary setting for fixed mode)</span>}
          </h3>
          <div className={`${isFixedPricingMode ? '' : 'grid grid-cols-2 gap-4'} text-sm`}>
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Default Margin:</span>
              {editingGlobalMargin ? (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={editMarginValue}
                    onChange={(e) => setEditMarginValue(e.target.value)}
                    className="w-20 px-2 py-1 border border-slate-300 rounded text-sm"
                    min="0"
                    max="500"
                  />
                  <span className="text-slate-500">%</span>
                  <button
                    onClick={handleSaveGlobalMargin}
                    disabled={saving}
                    className="p-1 text-green-600 hover:bg-green-50 rounded"
                    title="Save"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setEditingGlobalMargin(false)}
                    className="p-1 text-slate-400 hover:bg-slate-100 rounded"
                    title="Cancel"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="font-medium">{globalConfig.defaultMargin || 95}%</span>
                  <button
                    onClick={startEditingGlobalMargin}
                    className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                    title="Edit margin"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            {/* Only show pricing matrix status in matrix mode */}
            {!isFixedPricingMode && (
              <div>
                <span className="text-slate-500">Pricing Matrix:</span>
                <span className={`ml-2 font-medium ${globalConfig.usePricingMatrix ? 'text-green-600' : 'text-slate-500'}`}>
                  {globalConfig.usePricingMatrix ? 'Enabled' : 'Disabled (Flat Margin)'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      {!globalConfig && (
        <div className="border border-slate-200 rounded-lg p-4 text-slate-500 text-sm">
          No global configuration found. Using default 95% margin.
        </div>
      )}

      {/* Price Tiers - Hidden in fixed pricing mode */}
      {!isFixedPricingMode && (
        <div>
          <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Price Tiers
          </h3>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Tier</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Cost Range</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Margin %</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tiers.map((tier) => (
                  <TierRow key={tier.configId} tier={tier} onSave={handleSaveRule} saving={saving} />
                ))}
                {tiers.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-slate-500">
                      No price tiers configured
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Category Adjustments - Hidden in fixed pricing mode */}
      {!isFixedPricingMode && (
        <div>
          <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
            <Tag className="w-4 h-4" />
            Category Adjustments
          </h3>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Category</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Adjustment</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {categories.map((cat) => (
                  <CategoryRow key={cat.configId} category={cat} onSave={handleSaveRule} saving={saving} />
                ))}
                {categories.length === 0 && (
                  <tr>
                    <td colSpan="3" className="px-4 py-8 text-center text-slate-500">
                      No category adjustments configured
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Price Floors - Hidden in fixed pricing mode */}
      {!isFixedPricingMode && (
        <div>
          <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Price Floors
          </h3>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Category</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Minimum Price</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {floors.map((floor) => (
                  <FloorRow key={floor.configId} floor={floor} onSave={handleSaveRule} saving={saving} />
                ))}
                {floors.length === 0 && (
                  <tr>
                    <td colSpan="3" className="px-4 py-8 text-center text-slate-500">
                      No price floors configured
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Image Licenses */}
      <div>
        <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
          <DollarSign className="w-4 h-4" />
          Image Licenses
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          License fees are added to product prices based on the image source (e.g., World Illustrated).
        </p>
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Image Source</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">License Fee</th>
                <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {licenses.map((license) => (
                <LicenseRow key={license.configId} license={license} onSave={handleSaveRule} saving={saving} />
              ))}
              {licenses.length === 0 && (
                <tr>
                  <td colSpan="3" className="px-4 py-8 text-center text-slate-500">
                    No image licenses configured
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Price Calculator Preview */}
      <div className="border border-slate-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-slate-700 mb-4 flex items-center gap-2">
          <Calculator className="w-4 h-4" />
          Price Calculator Preview
        </h3>
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Base Cost ($)</label>
            <input
              type="number"
              value={previewCost}
              onChange={(e) => setPreviewCost(e.target.value)}
              step="0.01"
              min="0"
              className="w-32 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {/* Only show category selector if using matrix pricing */}
          {!isFixedPricingMode && (
            <div>
              <label className="block text-xs text-slate-500 mb-1">Category</label>
              <select
                value={previewCategory}
                onChange={(e) => setPreviewCategory(e.target.value)}
                className="w-40 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">None</option>
                <option value="t-shirt">T-Shirt</option>
                <option value="hoodie">Hoodie</option>
                <option value="mug">Mug</option>
                <option value="poster">Poster</option>
                <option value="sticker">Sticker</option>
                <option value="phone-case">Phone Case</option>
              </select>
            </div>
          )}
          {/* Fixed pricing mode: instant calculation */}
          {isFixedPricingMode ? (
            <div className="flex items-center gap-4 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
              <div>
                <span className="text-xs text-slate-500">Final Price:</span>
                <span className="ml-2 text-lg font-bold text-green-700">
                  ${(parseFloat(previewCost || 0) * (1 + (globalConfig?.defaultMargin || 95) / 100)).toFixed(2)}
                </span>
              </div>
              <div>
                <span className="text-xs text-slate-500">Margin:</span>
                <span className="ml-2 font-medium text-slate-700">{globalConfig?.defaultMargin || 95}%</span>
              </div>
              <div>
                <span className="text-xs text-slate-500">Mode:</span>
                <span className="ml-2 font-medium text-amber-600">Fixed</span>
              </div>
            </div>
          ) : (
            <>
              <button
                onClick={handlePreview}
                disabled={calculating}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {calculating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Calculator className="w-4 h-4" />
                )}
                Calculate
              </button>
              {previewResult && !previewResult.error && (
                <div className="flex items-center gap-4 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
                  <div>
                    <span className="text-xs text-slate-500">Final Price:</span>
                    <span className="ml-2 text-lg font-bold text-green-700">${previewResult.finalPrice}</span>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Margin:</span>
                    <span className="ml-2 font-medium text-slate-700">{previewResult.marginPercent}%</span>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Tier:</span>
                    <span className="ml-2 font-medium text-slate-700">{previewResult.tier || 'N/A'}</span>
                  </div>
                </div>
              )}
              {previewResult?.error && (
                <div className="text-sm text-red-600">{previewResult.error}</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Tier Row Component
function TierRow({ tier, onSave, saving }) {
  const [editing, setEditing] = useState(false);
  const [margin, setMargin] = useState(tier.marginPercent || 100);

  const handleSave = () => {
    onSave('TIER', tier.configId, { marginPercent: parseFloat(margin) });
    setEditing(false);
  };

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3 font-medium text-slate-900 capitalize">
        {tier.configId || tier.name || 'Unknown'}
      </td>
      <td className="px-4 py-3 text-slate-600">
        ${tier.minCost || 0} - ${tier.maxCost === null || tier.maxCost === undefined ? '∞' : `$${tier.maxCost}`}
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <input
            type="number"
            value={margin}
            onChange={(e) => setMargin(e.target.value)}
            className="w-20 px-2 py-1 border border-slate-200 rounded text-sm"
          />
        ) : (
          <span className="text-slate-900">{tier.marginPercent}%</span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {editing ? (
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setEditing(false)}
              className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              Save
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="px-2 py-1 text-xs text-blue-600 hover:text-blue-700"
          >
            Edit
          </button>
        )}
      </td>
    </tr>
  );
}

// Category Row Component
function CategoryRow({ category, onSave, saving }) {
  const [editing, setEditing] = useState(false);
  // DB uses marginAdjustment
  const [adjustment, setAdjustment] = useState(category.marginAdjustment || 0);

  const handleSave = () => {
    onSave('CATEGORY', category.configId, { marginAdjustment: parseFloat(adjustment) });
    setEditing(false);
  };

  const displayAdjustment = category.marginAdjustment || 0;

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3 font-medium text-slate-900 capitalize">
        {category.configId || category.name || 'Unknown'}
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <input
            type="number"
            value={adjustment}
            onChange={(e) => setAdjustment(e.target.value)}
            className="w-20 px-2 py-1 border border-slate-200 rounded text-sm"
          />
        ) : (
          <span className={displayAdjustment >= 0 ? 'text-green-600' : 'text-red-600'}>
            {displayAdjustment >= 0 ? '+' : ''}{displayAdjustment}%
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {editing ? (
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setEditing(false)}
              className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              Save
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="px-2 py-1 text-xs text-blue-600 hover:text-blue-700"
          >
            Edit
          </button>
        )}
      </td>
    </tr>
  );
}

// Floor Row Component
function FloorRow({ floor, onSave, saving }) {
  const [editing, setEditing] = useState(false);
  // minimumPrice is stored in cents in the database
  const [minPrice, setMinPrice] = useState(((floor.minimumPrice || 0) / 100).toFixed(2));

  const handleSave = () => {
    // Convert dollars back to cents for storage
    onSave('FLOOR', floor.configId, { minimumPrice: Math.round(parseFloat(minPrice) * 100) });
    setEditing(false);
  };

  // Get display price in dollars
  const displayPrice = ((floor.minimumPrice || 0) / 100).toFixed(2);

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3 font-medium text-slate-900 capitalize">
        {floor.configId || floor.category || 'Unknown'}
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <input
            type="number"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
            step="0.01"
            className="w-24 px-2 py-1 border border-slate-200 rounded text-sm"
          />
        ) : (
          <span className="text-slate-900">${displayPrice}</span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {editing ? (
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setEditing(false)}
              className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              Save
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="px-2 py-1 text-xs text-blue-600 hover:text-blue-700"
          >
            Edit
          </button>
        )}
      </td>
    </tr>
  );
}

// License Row Component
function LicenseRow({ license, onSave, saving }) {
  const [editing, setEditing] = useState(false);
  // flatFee is stored in cents in the database
  const [flatFee, setFlatFee] = useState(((license.flatFee || 0) / 100).toFixed(2));

  const handleSave = () => {
    // Convert dollars back to cents for storage
    onSave('LICENSE', license.configId, { flatFee: Math.round(parseFloat(flatFee) * 100) });
    setEditing(false);
  };

  // Get display fee in dollars
  const displayFee = ((license.flatFee || 0) / 100).toFixed(2);

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3">
        <div className="font-medium text-slate-900">{license.licenseName || license.configId}</div>
        {license.description && (
          <div className="text-xs text-slate-500 mt-0.5">{license.description}</div>
        )}
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <input
            type="number"
            value={flatFee}
            onChange={(e) => setFlatFee(e.target.value)}
            step="0.01"
            min="0"
            className="w-24 px-2 py-1 border border-slate-200 rounded text-sm"
          />
        ) : (
          <span className={license.flatFee > 0 ? 'text-amber-600 font-medium' : 'text-slate-500'}>
            {license.flatFee > 0 ? `$${displayFee}` : 'Free'}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {editing ? (
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setEditing(false)}
              className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              Save
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="px-2 py-1 text-xs text-blue-600 hover:text-blue-700"
          >
            Edit
          </button>
        )}
      </td>
    </tr>
  );
}

export default PricingTab;
